// Authentication & Authorization related constants and functions.

/** Valid permissions. */
export type PermissionT

  /** Allows to do a system-wide data export (all users activities info,
   *  all sessions / experiences, etc.) in CSV form. */
  = | 'ADMIN_DATA_EXPORT'

    /** Permits acccess to the main AI app. */
    | 'AI_APP_ACCESS'

    // Permit to create / edit blog posts.
    | 'BLOG_EDITOR'

    // Permits access to the "Content Management System" segment of the app,
    // intended for admin, and practitioners.
    | 'CMS_APP'

    /**
     * Permits to edit details of any video (no matter the practitioner who
     * owns it).
     * + It also allows to edit the AI Video Analysis field.
     */
    | 'EDIT_ANY_VIDEO_DETAILS'

    /** Permits to change artistId of content videos, thus transfering video
     *  ownership between practitioners. */
    | 'EDIT_VIDEO_OWNERSHIP'

    /** Permits to access the  */
    | 'PRACTITIONER_APPLICATION'

    /** Gives permission to list, modify, create user (and practitioners,
     *  in particular). */
    | 'USER_MANAGEMENT'

    /** Gives permission to upload videos, and to see / manage the videos you have
     *  uploaded. Mind that the upload to work also requires the user to be correctly
     *  promoted to practitioner (to have corresponding Artist record in DB). */
    | 'VIDEO_UPLOAD';

/** Valid user roles (each role is essentially a set of permissions, that may
 *  be changed during the app lifetime, as only user roles are stored in DB for
 *  each user, and the corresponding permission sets are deduced on runtime). */
export const ROLES = [
  'ARTIST',

  /**
   * It is assigned to inactivated artists instead of the ARTIST role,
   * to faciliate their identification in the database (without aggregation
   * across several collections).
   */
  'ARTIST_INACTIVE',

  'BETA_TESTER',
  'OWNER',

  /**
   * It is assigned to inactivated admin users instead of the OWNER role,
   * to faciliate their identification in the database (without looking
   * at the `adminSince` field).
   */
  'OWNER_INACTIVE',

  'PRACTITIONER_APPLICANT',
] as const;

const ROLE_SET = new Set(ROLES);
export type RoleT = typeof ROLES[number];

/** Permissions granted by each role. */
export const ROLE_PERMISSIONS: Record<RoleT, PermissionT[]> = {
  ARTIST: ['AI_APP_ACCESS', 'CMS_APP', 'VIDEO_UPLOAD'],
  ARTIST_INACTIVE: ['AI_APP_ACCESS'],
  BETA_TESTER: ['AI_APP_ACCESS'],
  OWNER: [
    'ADMIN_DATA_EXPORT',
    'AI_APP_ACCESS',
    'BLOG_EDITOR',
    'CMS_APP',
    'EDIT_ANY_VIDEO_DETAILS',
    'EDIT_VIDEO_OWNERSHIP',
    'USER_MANAGEMENT',
  ],
  OWNER_INACTIVE: ['AI_APP_ACCESS'],
  PRACTITIONER_APPLICANT: ['PRACTITIONER_APPLICATION'],
};

/**
 * Returns "true" if the given string is a valid role; "false" otherwise.
 */
export function isRole(string: string | undefined): string is RoleT {
  return string !== undefined && (ROLE_SET as Set<string>).has(string);
}

/**
 * Returns "true" if all strings in the given array are valid role values;
 * "false" otherwise.
 */
export function isRoleArray(strings: string[]): strings is RoleT[] {
  for (const string of strings) if (!isRole(string)) return false;
  return true;
}

/**
 * Roles related to the admin status management.
 */
export const ADMIN_ROLES: RoleT[] = ['OWNER', 'OWNER_INACTIVE'];

/**
 * Returns "true" if `role` relates to the admin status management;
 * returns "false" otherwise.
 *
 * BEWARE: "true" result does not mean that this is an "active admin" role!
 */
export function isAdminRole(role: RoleT): boolean {
  return ADMIN_ROLES.includes(role);
}

/**
 * Roles related to the artist status management.
 */
export const ARTIST_ROLES: RoleT[] = [
  'ARTIST',
  'ARTIST_INACTIVE',
  'PRACTITIONER_APPLICANT',
];

/**
 * Returns "true" if `role` relates to the artist (practitioner) status
 * management; returns "false" otherwise.
 *
 * BEWARE: "true" result does not mean that this is
 * an "approved, and active artist" role!
 */
export function isArtistRole(role: RoleT): boolean {
  return ARTIST_ROLES.includes(role);
}

export type PermissionSetT = Partial<Record<PermissionT, true>>;

/**
 * Given an array of user role strings it calculates the set of corresponding
 * user permissions. It throws if any input string does not match a valid user
 * role.
 * @return Object with "true" values for each granted permission key.
 */
export function calcPermissions(roles: RoleT[] = []): PermissionSetT {
  const res: PermissionSetT = {};
  for (const role of roles) {
    for (const p of ROLE_PERMISSIONS[role]) res[p] = true;
  }
  return res;
}

export type EvalPasswordStrengthResT = {
  has8Characters: boolean | undefined;
  hasCasesAndDigits: boolean | undefined;
  hasDigits: boolean | undefined;
  hasLowerCase: boolean | undefined;
  hasNoForbidden: boolean | undefined;
  hasNonAlphaNum: boolean | undefined;
  hasUpperCase: boolean | undefined;

  /* The overall password strength, an integer within [-1, 4] range (inclusive),
   * with -1 corresponding to empty password. Across our system we consider
   * passwords with the strength of 3 or 4 as secure. */
  strength: number;
};

/**
 * Our custom evaluator of password strength.
 */
export function evalPasswordStrength(
  password: string,
  forbidden: string[],
): EvalPasswordStrengthResT {
  let has8Characters: boolean | undefined;
  let hasCasesAndDigits: boolean | undefined;
  let hasDigits: boolean | undefined;
  let hasLowerCase: boolean | undefined;
  let hasNoForbidden: boolean | undefined;
  let hasNonAlphaNum: boolean | undefined;
  let hasUpperCase: boolean | undefined;
  let strength = -1;

  if (password) {
    strength = 0;

    const lowercased = password.toLowerCase();

    has8Characters = password.length >= 8;
    hasDigits = !!password.match(/\d/);
    hasLowerCase = password !== password.toUpperCase();
    hasNonAlphaNum = !!password.replace(/ /g, '').match(/\W/);
    hasUpperCase = password !== lowercased;

    hasCasesAndDigits = hasDigits && hasLowerCase && hasUpperCase;

    hasNoForbidden = true;
    for (const word of forbidden) {
      if (word.length > 3 && lowercased.includes(word.toLowerCase())) {
        hasNoForbidden = false;
        break;
      }
    }

    if (has8Characters) ++strength;
    if (hasDigits) ++strength;
    if (hasLowerCase && hasUpperCase) ++strength;
    if (hasNonAlphaNum) ++strength;

    if (strength > 2 && !(has8Characters && hasNoForbidden)) strength = 2;
  }

  return {
    has8Characters,
    hasCasesAndDigits,
    hasDigits,
    hasLowerCase,
    hasNoForbidden,
    hasNonAlphaNum,
    hasUpperCase,
    strength,
  };
}
